import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

// Multi-select aware focus state for the Tree + Gantt.
//
//   - selectedIds: the full set of selected items.  All visual highlighting,
//     swipe gestures, and keyboard shortcuts apply to every member.
//   - anchorId: the "primary" selected item.  Used as the start for shift-
//     range selection, the keyboard-nav cursor, and the entity the detail
//     pane shows when it's opened.  Always either null or a member of
//     selectedIds.
//   - openId: which item the right-hand detail pane is showing.  Orthogonal
//     to selection — only set when the user explicitly clicks `…` on a row.
//   - stickyShift / stickyCtrl: latched modifiers driven by the on-screen
//     mobile bar.  Cleared automatically after a row tap consumes them so
//     a phone-only user doesn't have to remember to untoggle the keys.
//
// Backwards-compat shims (`selectedId`, `select`) map to the anchor so the
// gradual migration of older call sites keeps compiling.

type SelectionCtx = {
  selectedIds: ReadonlySet<string>;
  anchorId: string | null;

  selectOne: (id: string | null) => void;
  toggleInSelection: (id: string) => void;
  /** Select every id from the current anchor to `toId` (inclusive),
   *  using `orderedIds` as the visible-order reference list. */
  selectRange: (toId: string, orderedIds: string[]) => void;
  clearSelection: () => void;

  openId: string | null;
  open: (id: string | null) => void;

  stickyShift: boolean;
  stickyCtrl: boolean;
  toggleStickyShift: () => void;
  toggleStickyCtrl: () => void;
  /** Read the current latched modifiers and clear them.  Used by row tap
   *  handlers so a single mobile chord = one row action. */
  consumeStickyModifiers: () => { shift: boolean; ctrl: boolean };

  // Compatibility shims — equivalent to anchorId / selectOne.
  selectedId: string | null;
  select: (id: string | null) => void;
};

const Ctx = createContext<SelectionCtx | null>(null);

export function SelectionProvider({ children }: { children: ReactNode }) {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [stickyShift, setStickyShift] = useState(false);
  const [stickyCtrl, setStickyCtrl] = useState(false);

  // We previously used two cross-coupled useEffects to keep `openId` and
  // `anchorId` in sync — "if openId changes, follow it with anchorId" plus
  // "if anchorId changes while pane is open, follow it with openId".  That
  // worked for one direction at a time but produced an infinite re-render
  // loop when both changed in the same commit (eg. createItem + openDetail
  // from the Gantt long-press, where the previously-open item's id was
  // still the anchor from a render earlier).  Each effect would see the
  // OTHER one's stale-by-one-render value and undo it.
  //
  // The mutators below now keep both invariants themselves, so we can
  // drop the effects entirely.  The "pane follows the anchor while it's
  // open" behaviour lives in `setOpenIfOpen` — every selection setter
  // calls it after writing the new anchor.

  const setOpenIfOpen = useCallback((id: string | null) => {
    setOpenId((cur) => (cur !== null ? id : cur));
  }, []);

  const selectOne = useCallback(
    (id: string | null) => {
      setAnchorId(id);
      setSelectedIds(id === null ? new Set() : new Set([id]));
      setOpenIfOpen(id);
    },
    [setOpenIfOpen],
  );

  const toggleInSelection = useCallback(
    (id: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      setAnchorId(id);
      setOpenIfOpen(id);
    },
    [setOpenIfOpen],
  );

  const selectRange = useCallback(
    (toId: string, orderedIds: string[]) => {
      // Without an anchor, treat the range as a single-pick.
      setSelectedIds((prev) => {
        const startId = anchorId ?? toId;
        const a = orderedIds.indexOf(startId);
        const b = orderedIds.indexOf(toId);
        if (a < 0 || b < 0) {
          // Anchor isn't visible; fall back to single-select.
          const fresh = new Set<string>([toId]);
          return fresh;
        }
        const [lo, hi] = a <= b ? [a, b] : [b, a];
        const next = new Set(prev);
        for (let i = lo; i <= hi; i++) {
          const id = orderedIds[i];
          if (id) next.add(id);
        }
        return next;
      });
      // Anchor stays where it was so the user can extend the range
      // further.  Pane follows the *new edge* — that's what the viewer is
      // looking at right now.
      setOpenIfOpen(toId);
    },
    [anchorId, setOpenIfOpen],
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setAnchorId(null);
    // Don't auto-close the pane on clearSelection — clearing the multi-
    // selection set is independent of the pane being open.
  }, []);

  // Opening implicitly selects (pane and selection always agree).  Passing
  // null closes the pane but leaves the existing selection alone.
  const open = useCallback((id: string | null) => {
    setOpenId(id);
    if (id !== null) {
      setAnchorId(id);
      setSelectedIds((prev) => (prev.has(id) ? prev : new Set([id])));
    }
  }, []);

  const toggleStickyShift = useCallback(() => {
    setStickyShift((v) => !v);
    setStickyCtrl(false);
  }, []);
  const toggleStickyCtrl = useCallback(() => {
    setStickyCtrl((v) => !v);
    setStickyShift(false);
  }, []);

  // We mirror sticky state in refs so `consumeStickyModifiers` can read +
  // reset without scheduling an extra render before the consumer's own
  // setState (which would race the consume).
  const stickyRef = useRef({ shift: false, ctrl: false });
  stickyRef.current = { shift: stickyShift, ctrl: stickyCtrl };
  const consumeStickyModifiers = useCallback(() => {
    const { shift, ctrl } = stickyRef.current;
    if (shift) setStickyShift(false);
    if (ctrl) setStickyCtrl(false);
    return { shift, ctrl };
  }, []);

  const value = useMemo<SelectionCtx>(
    () => ({
      selectedIds,
      anchorId,
      selectOne,
      toggleInSelection,
      selectRange,
      clearSelection,
      openId,
      open,
      stickyShift,
      stickyCtrl,
      toggleStickyShift,
      toggleStickyCtrl,
      consumeStickyModifiers,
      selectedId: anchorId,
      select: selectOne,
    }),
    [
      selectedIds,
      anchorId,
      selectOne,
      toggleInSelection,
      selectRange,
      clearSelection,
      openId,
      open,
      stickyShift,
      stickyCtrl,
      toggleStickyShift,
      toggleStickyCtrl,
      consumeStickyModifiers,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSelection(): SelectionCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSelection must be used within SelectionProvider");
  return ctx;
}
